import { useCallback, useMemo } from 'react'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
} from '../utils/generateAnalyticsQuery.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const MemberActivitiesScore = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', in: ['GROUP', 'BROADCAST'] },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [
        {
          field: 'count(reply) + count(post) + count(react)',
          as: 'Activities',
        },
      ],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    if (timeFrame.id === 'allTime') {
      return [generateAnalyticsQuery(currentQuery)]
    }

    return [
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Score"
          title={$t({
            id: 'Analytics.MemberActivitiesScore.ChartTitle',
            defaultMessage: 'Activities',
          })}
          description={timeFrame.text}
          current={getValueFromAnalyticsData(data[0], 'Activities')}
          previous={getValueFromAnalyticsData(data[1], 'Activities')}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
